'use client';

import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { useGetAllStrategiesFilterOptionsQuery } from 'src/api/redux/strategy/allStrategiesApi';
import { useDebounce } from 'use-debounce';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import {
  useClickOutside,
  useDropdownPosition,
  useResizeClose,
} from 'src/hooks/common';
import {
  FilterDropdownButton,
  FilterDropdownPortal,
  CheckboxListItem,
  DropdownEmptyState,
} from '../shared';

interface State {
  id: string;
  name: string;
}

const StrategyStateDropdown: React.FC = (): React.ReactElement => {
  const {
    boundedTerritory,
    selectedTerritory,
    selectedChainIds,
    selectedStates,
    setSelectedStates,
  } = useStrategies(LOCATION.STRATEGY_CENTER);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [allStates, setAllStates] = useState<State[]>([]);
  const isLoadingNextPage = useRef<boolean>(false);

  const { data: states, isFetching } = useGetAllStrategiesFilterOptionsQuery(
    {
      type: 'state',
      chain_id: selectedChainIds,
      territory_id: selectedTerritory?.id,
      bounding_territory: boundedTerritory?.id,
      q: debouncedSearch,
      page,
      pageSize,
    },
    { skip: selectedChainIds.length === 0 }
  );

  const hasMore = states?.results?.length === pageSize;

  const { dropdownStyle, calculatePosition } = useDropdownPosition(
    buttonRef,
    300
  );

  useClickOutside([buttonRef, dropdownRef], () => setIsOpen(false), isOpen);
  useResizeClose(() => setIsOpen(false), isOpen);

  useEffect(() => {
    setPage(1);
    setAllStates([]);
    isLoadingNextPage.current = false;
  }, [debouncedSearch, selectedChainIds, boundedTerritory?.id, selectedTerritory?.id]);

  useEffect(() => {
    if (states?.results) {
      setAllStates((prev) => {
        const validStates = states.results.filter((s: State) => s.name);
        if (page === 1) {
          isLoadingNextPage.current = false;
          return validStates;
        }
        const existingIds = new Set(prev.map((s: State) => s.id));
        const newStates = validStates.filter(
          (s: State) => !existingIds.has(s.id)
        );
        isLoadingNextPage.current = false;
        return [...prev, ...newStates];
      });
    }
  }, [states, page]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetching || !hasMore || isLoadingNextPage.current)
      return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      isLoadingNextPage.current = true;
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isOpen) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isOpen]);

  const isDisabled = selectedChainIds.length === 0;

  const handleClick = () => {
    if (isDisabled) return;

    if (!isOpen) {
      calculatePosition();
    }

    setIsOpen(!isOpen);
  };

  const handleStateToggle = (stateId: string) => {
    const updatedStates = selectedStates.includes(stateId)
      ? selectedStates.filter((s) => s !== stateId)
      : [...selectedStates, stateId];
    setSelectedStates(updatedStates);
  };

  const handleClearStates = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedStates([]);
  };

  const getDisplayText = () => {
    if (selectedStates.length === 0) return 'States';
    if (selectedStates.length === 1) {
      return (
        allStates.find((s) => s.id === selectedStates[0])?.name ||
        selectedStates[0]
      );
    }
    return `${selectedStates.length} States`;
  };

  return (
    <>
      <FilterDropdownButton
        icon={faLocationDot}
        label="State"
        isOpen={isOpen}
        isDisabled={isDisabled}
        displayText={getDisplayText()}
        hasSelection={selectedStates.length > 0}
        onClear={handleClearStates}
        onClick={handleClick}
        buttonRef={buttonRef}
        testId="strategy-state-dropdown"
      />

      <FilterDropdownPortal
        isOpen={isOpen}
        dropdownRef={dropdownRef}
        scrollContainerRef={scrollContainerRef}
        dropdownStyle={dropdownStyle}
        searchPlaceholder="Search states..."
        searchValue={search}
        onSearchChange={setSearch}
        selectedCount={selectedStates.length}
        onClearAll={() => setSelectedStates([])}
      >
        {allStates.map((state) => (
          <CheckboxListItem
            key={state.id}
            id={state.id}
            label={state.name}
            isSelected={selectedStates.includes(state.id)}
            onToggle={handleStateToggle}
          />
        ))}

        <DropdownEmptyState
          hasResults={allStates.length > 0}
          isLoading={isFetching}
          searchTerm={search}
          emptyMessage="states"
        />
      </FilterDropdownPortal>
    </>
  );
};

export default StrategyStateDropdown;
